'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import MetricCard from '@/components/ui/MetricCard';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface KPIs {
  totale_dispositivi: number;
  dispositivi_online: number;
  dispositivi_offline: number;
  dispositivi_manutenzione: number;
  conflitti_ip: number;
  ip_totali: number;
  ip_assegnati: number;
  ip_disponibili: number;
  subnet_totali: number;
  vlan_totali: number;
  uptime_medio: number;
}

export default function KPIBentoGrid() {
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pulse, setPulse] = useState(false);
  const prevKpisRef = useRef<KPIs | null>(null);

  const fetchKPIs = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_kpis');
      if (error) throw error;
      const newKpis = data as KPIs;
      // Detect change to trigger pulse
      if (prevKpisRef.current && JSON.stringify(prevKpisRef.current) !== JSON.stringify(newKpis)) {
        setPulse(true);
        setTimeout(() => setPulse(false), 1200);
      }
      prevKpisRef.current = newKpis;
      setKpis(newKpis);
      setLastUpdated(new Date());
    } catch {
      // Fallback: fetch manually
      try {
        const [
          { count: totale },
          { count: online },
          { count: offline },
          { count: conflitti },
          { count: ipTotali },
          { count: ipAssegnati },
        ] = await Promise.all([
          supabase.from('devices').select('*', { count: 'exact', head: true }),
          supabase.from('devices').select('*', { count: 'exact', head: true }).eq('stato', 'online'),
          supabase.from('devices').select('*', { count: 'exact', head: true }).eq('stato', 'offline'),
          supabase.from('ip_addresses').select('*', { count: 'exact', head: true }).eq('stato', 'conflitto'),
          supabase.from('ip_addresses').select('*', { count: 'exact', head: true }),
          supabase.from('ip_addresses').select('*', { count: 'exact', head: true }).eq('stato', 'assegnato'),
        ]);
        const fallback: KPIs = {
          totale_dispositivi: totale || 0,
          dispositivi_online: online || 0,
          dispositivi_offline: offline || 0,
          dispositivi_manutenzione: 0,
          conflitti_ip: conflitti || 0,
          ip_totali: ipTotali || 0,
          ip_assegnati: ipAssegnati || 0,
          ip_disponibili: (ipTotali || 0) - (ipAssegnati || 0),
          subnet_totali: 0,
          vlan_totali: 0,
          uptime_medio: 0,
        };
        if (prevKpisRef.current && JSON.stringify(prevKpisRef.current) !== JSON.stringify(fallback)) {
          setPulse(true);
          setTimeout(() => setPulse(false), 1200);
        }
        prevKpisRef.current = fallback;
        setKpis(fallback);
        setLastUpdated(new Date());
      } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKPIs();

    // Real-time subscriptions on all relevant tables
    const channel = supabase
      .channel('kpi_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'devices' }, fetchKPIs)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ip_addresses' }, fetchKPIs)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'subnets' }, fetchKPIs)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'allarmi' }, fetchKPIs)
      .subscribe();

    // Polling fallback every 30 seconds
    const interval = setInterval(fetchKPIs, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [fetchKPIs]);

  const ipUtilPct = kpis && kpis.ip_totali > 0
    ? Math.round((kpis.ip_assegnati / kpis.ip_totali) * 100)
    : 0;

  const uptimeGiorni = kpis ? Math.floor((kpis.uptime_medio || 0) / 24) : 0;

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 h-5" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl p-5 h-28 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Real-time status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${pulse ? 'bg-sky-400 animate-ping' : 'bg-green-400'}`} />
          <span className="text-xs text-muted-color font-mono">
            {pulse ? 'Aggiornamento in corso...' : lastUpdated ? `KPI aggiornati alle ${formatTime(lastUpdated)}` : 'In attesa di dati...'}
          </span>
        </div>
        <button
          onClick={fetchKPIs}
          className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 transition-colors"
        >
          <Icon name="ArrowPathIcon" size={13} />
          Aggiorna ora
        </button>
      </div>

      {/* KPI Grid */}
      <div className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-6 gap-4 transition-opacity duration-300 ${pulse ? 'opacity-80' : 'opacity-100'}`}>
        <div className="col-span-2 md:col-span-1 xl:col-span-2">
          <MetricCard
            label="Utilizzo IP"
            value={`${ipUtilPct}%`}
            subtext={`${kpis?.ip_assegnati || 0} di ${kpis?.ip_totali || 0} IP assegnati`}
            trend={{ direction: ipUtilPct > 80 ? 'up' : 'flat', value: ipUtilPct > 80 ? 'Soglia critica' : 'Nella norma', positive: ipUtilPct <= 80 }}
            icon="GlobeAltIcon"
            variant={ipUtilPct > 85 ? 'danger' : ipUtilPct > 70 ? 'warning' : 'default'}
            className="h-full"
          />
        </div>
        <MetricCard
          label="Conflitti IP"
          value={String(kpis?.conflitti_ip || 0)}
          subtext={kpis?.conflitti_ip ? 'Richiede intervento immediato' : 'Nessun conflitto rilevato'}
          trend={{ direction: (kpis?.conflitti_ip || 0) > 0 ? 'up' : 'flat', value: (kpis?.conflitti_ip || 0) > 0 ? 'Attenzione richiesta' : 'Tutto ok', positive: (kpis?.conflitti_ip || 0) === 0 }}
          icon="ExclamationTriangleIcon"
          variant={(kpis?.conflitti_ip || 0) > 0 ? 'danger' : 'default'}
          className="h-full"
        />
        <MetricCard
          label="Dispositivi Offline"
          value={String(kpis?.dispositivi_offline || 0)}
          subtext={`Su ${kpis?.totale_dispositivi || 0} dispositivi gestiti`}
          trend={{ direction: (kpis?.dispositivi_offline || 0) > 0 ? 'up' : 'flat', value: `${kpis?.dispositivi_online || 0} online`, positive: true }}
          icon="ServerIcon"
          variant={(kpis?.dispositivi_offline || 0) > 3 ? 'danger' : (kpis?.dispositivi_offline || 0) > 0 ? 'warning' : 'default'}
          className="h-full"
        />
        <MetricCard
          label="Dispositivi Online"
          value={String(kpis?.dispositivi_online || 0)}
          subtext={`${kpis?.totale_dispositivi ? Math.round(((kpis.dispositivi_online || 0) / kpis.totale_dispositivi) * 100) : 0}% del totale`}
          trend={{ direction: 'flat', value: 'Monitoraggio attivo', positive: true }}
          icon="CheckCircleIcon"
          variant="default"
          className="h-full"
        />
        <MetricCard
          label="Uptime Medio"
          value={`${uptimeGiorni}g`}
          subtext="Dispositivi online attivi"
          trend={{ direction: 'flat', value: 'Stabile', positive: true }}
          icon="ClockIcon"
          variant="default"
          className="h-full"
        />
        <MetricCard
          label="Manutenzione"
          value={String(kpis?.dispositivi_manutenzione || 0)}
          subtext="Dispositivi in manutenzione"
          trend={{ direction: 'flat', value: 'Programmata', positive: true }}
          icon="WrenchScrewdriverIcon"
          variant={(kpis?.dispositivi_manutenzione || 0) > 0 ? 'warning' : 'default'}
          className="h-full"
        />
      </div>
    </div>
  );
}